import * as React from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import MenuItem from "@mui/material/MenuItem";
import { useTranslation } from "react-i18next";

import { ApiContext } from "../../../App";

import {KEYS, KEY_TO_TEXT} from "../../../util/keys";


function PointEditForm(props) {
	const { value, onChange, errorMessages, tariffs, controllers, floorPlans } =
		props;

	const { t } = useTranslation();

	const handleChange = (props) => {
		onChange(props.target.id, props.target.value);
	};

	const handleDropDownChange = (field, event) => {
		onChange(field, event.target.value);
	};

	return value ? (
		<Stack spacing={2}>
			<TextField
				variant="standard"
				id="category"
				label={t("Category")}
				onChange={(event)=>handleDropDownChange("category", event)}
				value={value.category ?? ""}
				select
				helperText={errorMessages["category"] ? errorMessages["category"] : ""}
				error={errorMessages.hasOwnProperty("category")}
			>
				<MenuItem key={"INPUT"} value={"INPUT"}>
						{t("Input")}
				</MenuItem>
				<MenuItem key={"OUTPUT"} value={"OUTPUT"}>
						{t("Output")}
				</MenuItem>
			</TextField>
			<TextField
				variant="standard"
				id="key"
				label={t("Key")}
				onChange={(event)=>handleDropDownChange("key", event)}
				value={value.key ?? ""}
				select
				helperText={errorMessages["key"] ? errorMessages["key"] : ""}
				error={errorMessages.hasOwnProperty("key")}
			>
				{KEYS.map((option) => (
					<MenuItem
						key={option}
						value={option}
					>
						{t(KEY_TO_TEXT[option])}
					</MenuItem>
				))}
			</TextField>
			<TextField
				variant="standard"
				id="controller"
				label={t("Controller")}
				onChange={(event)=>handleDropDownChange("controller", event)}
				value={value.controller ?? ""}
				select
				helperText={
					errorMessages["controller"] ? errorMessages["controller"] : ""
				}
				error={errorMessages.hasOwnProperty("controller")}
			>
				{controllers.map((option) => (
					<MenuItem
						key={option.url}
						value={option.url}
					>
						{option.name}
					</MenuItem>
				))}
			</TextField>
			<TextField
				variant="standard"
				id="identifier"
				label={t("ID")}
				value={value.identifier ?? ""}
				onChange={handleChange}
				error={errorMessages.hasOwnProperty("identifier")}
				helperText={
					errorMessages["identifier"] ? errorMessages["identifier"] : ""
				}
			/>
			<TextField
				variant="standard"
				id="location"
				label={t("Location")}
				value={value.location ?? ""}
				onChange={handleChange}
				error={errorMessages.hasOwnProperty("location")}
				helperText={errorMessages["location"] ? errorMessages["location"] : ""}
			/>
			<TextField
				variant="standard"
				id="system"
				label={t("System")}
				value={value.system ?? ""}
				onChange={handleChange}
				error={errorMessages.hasOwnProperty("system")}
				helperText={errorMessages["system"] ? errorMessages["system"] : ""}
			/>
			<TextField
				variant="standard"
				id="name"
				label={t("Name")}
				value={value.name ?? ""}
				onChange={handleChange}
				error={errorMessages.hasOwnProperty("name")}
				helperText={errorMessages["name"] ? errorMessages["name"] : ""}
			/>
			
			{["POWER_METER", "GAS_METER"].includes(value.key) && (<TextField
						variant="standard"
						id="tariff"
						label={t("Tariff")}
						onChange={(event)=>handleDropDownChange("tariff", event)}
						value={value.tariff ?? ""}
						select
						helperText={errorMessages["tariff"] ? errorMessages["tariff"] : ""}
						error={errorMessages.hasOwnProperty("tariff")}
					>
						{tariffs.map((option) => (
							<MenuItem
								key={option.url}
								value={option.url}
							>
								{option.name}
							</MenuItem>
						))}
						<MenuItem key="no_tariff" value="">
							{t("None")}
						</MenuItem>
				</TextField>
			)}
			{(value.category === "INPUT") &&
				<TextField
					variant="standard"
					id="floor_plan"
					label={t("Floor plan")}
					onChange={(event)=>handleDropDownChange("floor_plan", event)}
					value={value.floor_plan ?? ""}
					select
					helperText={
						errorMessages["floor_plan"] ? errorMessages["floor_plan"] : ""
					}
					error={errorMessages.hasOwnProperty("floor_plan")}
				>
					{floorPlans.map((option) => (
						<MenuItem
							key={option.url}
							value={option.url}
						>
							{option.name}
						</MenuItem>
					))}
					{
						<MenuItem
							key={"none"}
							value={""}
						>
							{t("None")}
						</MenuItem>
					}
				</TextField>
			}	
			{(value.category === "INPUT") &&
				<FormControlLabel
					control={
						<Checkbox
							id="display_on_floorplan"
							checked={value.display_on_floorplan ?? false}
							onChange={(event) => {
								onChange("display_on_floorplan", event.target.checked);
							}}
							style={{ paddingLeft: 0, paddingRight: 3 }}
						/>
					}
					label={t("Display on floor plan")}
				/>
			}
		</Stack>
	) : null;
}

function PointEditPopUp(props) {
	const { open, onCloseWithoutSaving, onCloseWithUpdate } = props;

	const { t } = useTranslation();

	const [data, setData] = React.useState(props.data);
	const [alertOpen, setAlertOpen] = React.useState(false);
	const [alertMessage, setAlertMessage] = React.useState("");
	const [errorMessages, setErrorMessages] = React.useState({});
	const [tariffs, setTariffs] = React.useState(props.tariffs);
	const [controllers, setControllers] = React.useState(props.controllers);
	const [floorPlans, setFloorPlans] = React.useState(props.floorPlans);

	const theme = useTheme();
	const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

	const {pointsApi} = React.useContext(ApiContext)

	React.useEffect(() => {
		setData(props.data);
	}, [props.data]);

	React.useEffect(() => {
		setTariffs(props.tariffs);
	}, [props.tariffs]);

	React.useEffect(() => {
		setControllers(props.controllers);
	}, [props.controllers]);

	React.useEffect(() => {
		setFloorPlans(props.floorPlans);
	}, [props.floorPlans]);

	const handleFormChange = (id, value) => {

		setData((old) => ({ ...old, [id]: value }));
		

		setErrorMessages((old) =>
			Object.fromEntries(Object.entries(old).filter(([key]) => key !== id))
		);
	};

	const handleCloseWithoutSaving = () => {
		setData(props.data);
		onCloseWithoutSaving();
		setErrorMessages({});
	};

	const handleSave = () => {
		setErrorMessages({});
		
		var newData = { ...data };

		const onSuccess = (data) => {
			const updatedRow = { ...newData, url: data.url };
			onCloseWithUpdate(updatedRow);
		};

		const onError = (response) => {

			setErrorMessages(response);

			const non_field_errors = Object.entries(response)
				.filter(([key]) => key === "non_field_errors")
				.map(([key, value]) => value);
			if (non_field_errors.length > 0) {
				setAlertMessage(non_field_errors.join("; "));
				setAlertOpen(true);
			}

		};

		if (!data.hasOwnProperty("url")) {
			let payload = { ...newData };

			pointsApi.create(payload, onSuccess, onError);
		} else {
			let payload = { ...newData };
			delete payload.url;
			pointsApi.update(newData.url, payload, onSuccess, onError);
		}
	};

	return (
		<React.Fragment>
			<Dialog
				open={open}
				onClose={handleCloseWithoutSaving}
				fullScreen={fullScreen}
				fullWidth
				maxWidth={"md"}
			>
				<DialogTitle>{t("Edit point")}</DialogTitle>
				<DialogContent>
					<PointEditForm
						value={data}
						onChange={handleFormChange}
						errorMessages={errorMessages}
						tariffs={tariffs}
						controllers={controllers}
						floorPlans={floorPlans}
					/>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={handleCloseWithoutSaving}
						autoFocus
					>
						{t("Cancel")}
					</Button>
					<Button onClick={handleSave}>{t("Save")}</Button>
				</DialogActions>
			</Dialog>
			<Dialog
				open={alertOpen}
				onClose={() => setAlertOpen(false)}
			>
				<DialogTitle id="alert-dialog-title">
					{t("Problem saving data")}
				</DialogTitle>
				<DialogContent>
					<DialogContentText id="alert-dialog-description">
						{alertMessage}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => setAlertOpen(false)}
						autoFocus
					>
						{t("OK")}
					</Button>
				</DialogActions>
			</Dialog>
		</React.Fragment>
	);
}

export default PointEditPopUp;
